import {
  ShieldCheck,
  Users,
  Clock,
  Wallet,
  Home as HomeIcon,
  CalendarCheck,
} from "lucide-react";
import Hero from "@/components/site/Hero";
import TrustMarquee from "@/components/site/TrustMarquee";
import ServicesGrid from "@/components/site/ServicesGrid";
import ShiftModels from "@/components/site/ShiftModels";
import Testimonials from "@/components/site/Testimonials";
import FAQ from "@/components/site/FAQ";
import SectionHeader from "@/components/site/SectionHeader";
import WorkforceGallery from "@/components/site/WorkforceGallery";
import { Card } from "@/components/ui/card";
import { IMAGES } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const HIGHLIGHTS = [
  {
    icon: ShieldCheck,
    title: "Trusted manpower supplier",
    desc: "Long-term partnerships with multiple companies across industries.",
  },
  {
    icon: Users,
    title: "Skilled workforce deployment",
    desc: "Skilled, semi-skilled, and unskilled labor matched to your operations.",
  },
  {
    icon: Clock,
    title: "8-hour & 12-hour shift staffing",
    desc: "Flexible shift configurations for round-the-clock industrial operations.",
  },
  {
    icon: Wallet,
    title: "PF, ESI, Bonus & Increment",
    desc: "Full statutory compliance with on-time disbursements every cycle.",
  },
  {
    icon: HomeIcon,
    title: "Accommodation & meal support",
    desc: "Housing, tiffin, lunch, coffee, and dinner arranged for deployed workers.",
  },
  {
    icon: CalendarCheck,
    title: "On-time payroll compliance",
    desc: "Salary processed and disbursed before the 8th of every month.",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      <Hero />
      <TrustMarquee />

      {/* Highlights */}
      <section
        className="bg-white py-16 md:py-24"
        data-testid="home-highlights"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Why RK Enterprises"
            title="Built for industrial reliability"
            description="Compliance, discipline, and on-time deployment — at the core of every workforce we supply."
            testid="home-highlights-header"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {HIGHLIGHTS.map((h, i) => {
              const Icon = h.icon;
              return (
                <Card
                  key={h.title}
                  data-testid={`highlight-card-${i}`}
                  className="rk-lift p-6 sm:p-7 bg-white border border-[#E2E8F0] rounded-sm rk-fade-up"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-11 w-11 grid place-items-center rounded-sm bg-[#1F6B5C]/10 text-[#1F6B5C] shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-[#0F172A] text-lg">
                        {h.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-[#475569]">
                        {h.desc}
                      </p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <ServicesGrid />
      <WorkforceGallery />
      <ShiftModels />

      {/* Image strip CTA */}
      <section
        className="relative py-20 md:py-28 overflow-hidden"
        data-testid="home-cta-strip"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${IMAGES.factoryAssembly})` }}
        />
        <div className="absolute inset-0 bg-[#0F172A]/82" />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          <div className="rk-eyebrow text-[#67e8c4] !text-[#67e8c4]">
            Ready to scale your workforce?
          </div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
            Deploy compliant manpower in 48–72 hours
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-200 max-w-2xl mx-auto">
            From single shifts to full-plant deployments, RK Enterprises
            delivers reliable workforce solutions across India.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Button
              asChild
              data-testid="home-cta-strip-hire"
              className="bg-white text-[#1E3A8A] hover:bg-slate-100 rounded-sm h-12 px-6"
            >
              <Link to="/contact?intent=hire">
                Request Workforce
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              data-testid="home-cta-strip-apply"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-[#0F172A] rounded-sm h-12 px-6 bg-transparent"
            >
              <Link to="/careers">Apply for a Job</Link>
            </Button>
          </div>
        </div>
      </section>

      <Testimonials />
      <FAQ />
    </div>
  );
}
